import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Box, IconButton,
  useTheme, useMediaQuery, Drawer, styled, alpha, List, ListItem, ListItemText
} from '@mui/material';
import {
  Star, DarkMode, Menu as MenuIcon, Close
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

// Styled Components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(88, 28, 135, 0.1)',
  backdropFilter: 'blur(10px)',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  boxShadow: 'none',
}));

const StarField = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 0,
});

const AnimatedStar = styled(Box)<{ star: { x: number; y: number; size: number; opacity: number } }>(({ star }) => ({
  position: 'absolute',
  left: `${star.x}%`,
  top: `${star.y}%`,
  width: `${star.size}px`,
  height: `${star.size}px`,
  backgroundColor: 'white',
  borderRadius: '50%',
  opacity: star.opacity,
  animation: `twinkle ${2 + Math.random() * 3}s infinite`,
  animationDelay: `${Math.random() * 3}s`,
  '@keyframes twinkle': {
    '0%, 100%': { opacity: star.opacity },
    '50%': { opacity: star.opacity * 0.3 },
  },
}));

interface LayoutProps {
  children: React.ReactNode;
  showNavigation?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, showNavigation = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin, logout, user } = useAuth();
  const { isRTL } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  // Generate stars for background
  const stars = React.useMemo(() =>
    Array.from({ length: 50 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.8 + 0.2
    })), []
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigationItems = [
    { label: t('navigation.home'), href: '/' },
    { label: t('navigation.dashboard'), href: '/dashboard' },
    // { label: t('navigation.onboarding'), href: '/onboarding' },
    { label: t('navigation.chat'), href: '/chat' },
    { label: t('navigation.birthChart'), href: '/birth-chart' },
    { label: t('navigation.readingReport'), href: '/reading-report' },
  ];

  if (isAdmin) {
    navigationItems.push(
      { label: t('navigation.userManagement'), href: '/users' },
      { label: t('navigation.contentManagement'), href: '/content' }
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, 
          ${theme.palette.primary.dark} 0%, 
          ${theme.palette.secondary.dark} 50%, 
          ${theme.palette.primary.main} 100%)`,
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Animated Stars Background */}
      <StarField>
        {stars.map((star, index) => (
          <AnimatedStar key={index} star={star} />
        ))}
      </StarField>

      {/* Navigation */}
      {showNavigation && (
        <>
          <StyledAppBar position="fixed">
            <Container maxWidth="xl">
              <Toolbar sx={{ justifyContent: 'space-between', flexDirection: 'row-reverse' }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Star sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
                    <DarkMode sx={{
                      color: 'info.light',
                      fontSize: 16,
                      position: 'absolute',
                      top: -4,
                      right: -4
                    }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.primary.light})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {t('brand.name')}
                  </Typography>
                </Box>

                {/* Desktop Navigation */}
                {!isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {navigationItems.map((item, index) => (
                      <Button
                        key={index}
                        color="inherit"
                        component={Link}
                        to={item.href}
                        sx={{
                          textTransform: 'none',
                          '&:hover': { color: theme.palette.warning.main }
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}

                    {/* Language Switcher */}
                    <LanguageSwitcher />

                    {user ? (
                      <>
                        <Button color="inherit" onClick={handleLogout} sx={{ ml: 2 }}>
                          {t('auth.logout')}
                        </Button>
                        <Button color="inherit" component={Link} to="/change-password" sx={{ ml: 1 }}>
                          {t('auth.changePassword')}
                        </Button>
                      </>
                    ) : (
                      <Button color="inherit" component={Link} to="/login" sx={{ ml: 2 }}>
                        {t('auth.login')}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      color="inherit"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? <Close /> : <MenuIcon />}
                    </IconButton>
                    <LanguageSwitcher />
                  </Box>
                )}
              </Toolbar>
            </Container>
          </StyledAppBar>

          {/* Mobile Drawer */}
          <Drawer
            anchor={isRTL ? 'right' : 'right'}
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
              sx: {
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.primary.main, 0.95)} 0%, 
                  ${alpha(theme.palette.secondary.main, 0.95)} 100%)`,
                backdropFilter: 'blur(10px)',
                color: 'white',
                width: 250
              }
            }}
          >
            <List sx={{ pt: 4 }}>
              {navigationItems.map((item, index) => (
                <ListItem key={index} component={Link} to={item.href} onClick={() => setMobileMenuOpen(false)}>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
              {user ? (
                <>
                  <ListItem>
                    <Button color="inherit" fullWidth onClick={handleLogout} sx={{ textTransform: 'none' }}>
                      {t('auth.logout')}
                    </Button>
                  </ListItem>
                  <ListItem>
                    <Button color="inherit" component={Link} to="/change-password" fullWidth sx={{ textTransform: 'none' }}>
                      {t('auth.changePassword')}
                    </Button>
                  </ListItem>
                </>
              ) : (
                <ListItem>
                  <Button color="inherit" component={Link} to="/login" fullWidth sx={{ textTransform: 'none' }}>
                    {t('auth.login')}
                  </Button>
                </ListItem>
              )}
            </List>
          </Drawer>
        </>
      )}

      {/* Main Content */}
      <Box sx={{ 
        pt: showNavigation ? { xs: 8, sm: 9 } : 0,
        position: 'relative',
        zIndex: 1
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 